import React, { useEffect, useMemo, useRef } from "react";
import { observer } from "mobx-react";
import { SignedEditionDndaWorkflowStateViewStore } from "../../store";
import { SignedEditionDndaWorkflowStateView } from "../view/SignedEditionDndaWorkflowStateView";
import { SignedEditionDndaWorkflowStateUpdateStore } from "../../store";
import { SignedEditionDndaWorkflowStateUpdate } from "../update/SignedEditionDndaWorkflowStateUpdate";
import { useRootStore } from "../../../../store/contexts/RootStoreContext";
import { Box, CircularProgress } from "@mui/material";

export type SignedEditionDndaWorkflowStateModalContentProps = {
  entityId: string;
  mode: "view" | "update";
  onClose: () => void;
};

export const SignedEditionDndaWorkflowStateModalContent: React.FC<SignedEditionDndaWorkflowStateModalContentProps> =
  observer(({ entityId, mode, onClose }) => {
    const rootStore = useRootStore();
    const initializedRef = useRef<string | null>(null);

    const storeInfo = useMemo(() => {
      if (mode === "update") {
        const s = new SignedEditionDndaWorkflowStateUpdateStore(rootStore);
        s.isModalMode = true;
        return { store: s, type: "update" as const };
      }
      // Default to view mode
      const s = new SignedEditionDndaWorkflowStateViewStore(rootStore);
      s.isModalMode = true;
      return { store: s, type: "view" as const };
    }, [rootStore, mode]);

    useEffect(() => {
      if (entityId && initializedRef.current !== entityId) {
        initializedRef.current = entityId;
        storeInfo.store.initialize({ id: entityId });
      }
    }, [entityId, storeInfo]);

    useEffect(() => {
      return () => {
        storeInfo.store.destroy();
      };
    }, [storeInfo]);

    const store = storeInfo.store;
    if (store.status === "processing" || store.status === "uninitialized") {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 200,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box sx={{ p: 2 }}>
        {storeInfo.type === "view" && (
          <SignedEditionDndaWorkflowStateView
            signedEditionDndaWorkflowStateViewStore={
              storeInfo.store as SignedEditionDndaWorkflowStateViewStore
            }
          />
        )}
        {storeInfo.type === "update" && (
          <SignedEditionDndaWorkflowStateUpdate
            signedEditionDndaWorkflowStateUpdateStore={
              storeInfo.store as SignedEditionDndaWorkflowStateUpdateStore
            }
            isModalMode
            onModalClose={onClose}
          />
        )}
      </Box>
    );
  });
